import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShoppingBag } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";

export default function StorePage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  usePageTitle("Store");

  useEffect(() => {
    Promise.all([
      api.get("/content/products"),
      api.get("/content/product_categories"),
    ]).then(([p, c]) => {
      setProducts(p.data);
      setCategories(c.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filteredProducts = activeCategory === "All"
    ? products
    : products.filter(p => p.category === activeCategory);

  const allCategories = ["All", ...categories.map(c => c.name)];

  return (
    <div data-testid="store-page">
      {/* Header */}
      <div className="bg-jci-black py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">JCI Store</h1>
          <p className="text-white/50 mt-3 text-base">Official JCI Bangladesh merchandise and accessories</p>
        </div>
        <div className="h-1 bg-gradient-to-r from-jci-blue via-jci-teal to-jci-navy mt-8" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-16">
        {/* Category Filter */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10" data-testid="category-filter">
            {allCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                data-testid={`category-btn-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === cat
                    ? "bg-jci-blue text-white shadow-md shadow-blue-500/20"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {[1,2,3,4].map(i => <div key={i} className="h-80 bg-slate-100 rounded-2xl animate-pulse" />)}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20">
            <ShoppingBag className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-jci-black mb-2">{activeCategory === "All" ? "Coming Soon" : "No Products"}</h2>
            <p className="text-slate-500">
              {activeCategory === "All"
                ? "Our store is being set up. Check back soon for official JCI Bangladesh merchandise!"
                : `No products in the "${activeCategory}" category yet.`}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-testid="products-grid">
            {filteredProducts.map((p, i) => (
              <Card key={p.id} className="card-hover border-0 shadow-md overflow-hidden" data-testid={`product-card-${i}`}>
                <div className="overflow-hidden h-56">
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <CardContent className="p-5">
                  {p.category && <Badge variant="secondary" className="mb-2 bg-jci-blue/10 text-jci-blue text-xs">{p.category}</Badge>}
                  <h3 className="font-bold text-jci-black text-base mb-1">{p.name}</h3>
                  <p className="text-xs text-slate-500 mb-3 line-clamp-2">{p.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-jci-blue">BDT {p.price?.toLocaleString()}</span>
                    <Button size="sm" className="bg-jci-blue hover:bg-jci-navy text-white text-xs" disabled={!p.in_stock}>
                      {p.in_stock ? "Buy Now" : "Out of Stock"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
